import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useNotifyStatus } from "contexts/status";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useState } from "react";
import {
  type ScrapEyeSensorRead,
  useUpdateSensorMetadataMutation,
} from "src/store/api/generatedApi";

export const SensorMetadataDialog = ({
  sensor,
  onClose,
}: {
  sensor: ScrapEyeSensorRead;
  onClose: () => void;
}) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const [name, setName] = useState(sensor.name);
  const [propertiesText, setPropertiesText] = useState(() =>
    JSON.stringify(sensor.properties, null, 2)
  );
  const [updateSensorMetadata, { isLoading: isSavingMetadata }] =
    useUpdateSensorMetadataMutation();

  const saveMetadata = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      notifyStatus({ type: "error", text: t("sensorMetadataError") });
      return;
    }

    let properties: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(propertiesText);
      if (
        parsed == null ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        notifyStatus({ type: "error", text: t("sensorPropertiesObjectError") });
        return;
      }
      properties = parsed as Record<string, unknown>;
    } catch {
      notifyStatus({ type: "error", text: t("invalidJson") });
      return;
    }
    try {
      await updateSensorMetadata({
        tenantName,
        sensorId: sensor.id,
        scrapEyeSensorMetadataUpdate: {
          name: trimmedName,
          properties,
          expected_version_id: sensor.metadata_version_id,
        },
      }).unwrap();
      notifyStatus({ type: "success", text: t("changesSaved") });
      onClose();
    } catch (error) {
      notifyStatus({
        type: "error",
        text: fetchErrorDetail(error) ?? t("sensorMetadataError"),
      });
    }
  };

  return (
    <Dialog open onClose={isSavingMetadata ? undefined : onClose}>
      <DialogTitle>{t("edit")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2, width: { xs: "100%", sm: 380 } }}>
          <TextField
            fullWidth
            label={t("sensorNameLabel")}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            disabled={isSavingMetadata}
          />
          <TextField
            fullWidth
            multiline
            minRows={5}
            label={t("properties")}
            value={propertiesText}
            onChange={(event) => {
              setPropertiesText(event.target.value);
            }}
            disabled={isSavingMetadata}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSavingMetadata}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void saveMetadata()}
          disabled={isSavingMetadata}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
